'use client'

import React from 'react'
import Image from 'next/image'
import Link from 'next/link'
import { ArrowRight } from 'lucide-react'
import type { Media, Team } from '@/payload-types'
import { RichText } from '../RichText'
import { getMediaUrl } from '../../lib/media'
import { PartnerIntro } from './PartnerIntro'

interface PersonaOverviewProps {
  overviewHeading?: any
  overviewText?: any
  overviewPartner?: Team | null
  overviewPartnerIntro?: any
}

export function PersonaOverview({
  overviewHeading,
  overviewText,
  overviewPartner,
  overviewPartnerIntro,
}: PersonaOverviewProps) {
  if (!overviewHeading && !overviewText) {
    return null
  }

  const partnerPhoto = overviewPartner?.photo as Media | null | undefined

  return (
    <section className="py-12 md:py-20 px-6 md:px-12 bg-stone">
      <div className="max-w-380 mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center">
          {/* Left Column - Text Content */}
          <div className="space-y-6">
            {/* Heading */}
            {overviewHeading && (
              <RichText
                data={overviewHeading}
                className="text-2xl md:text-3xl leading-14 lg:text-3xl font-medium [&_em]:font-serif [&_em]:italic [&_em]:text-brand-gold [&_em]:not-italic [&_p]:leading-snug"
              />
            )}

            {/* Overview Text */}
            {overviewText && (
              <RichText
                data={overviewText}
                className="prose prose-lg max-w-none text-gray-700 [&_p]:mb-4 [&_p]:leading-relaxed"
              />
            )}
          </div>

          {/* Right Column - Your Attorney */}
          {overviewPartner && (
            <div className="space-y-5">
              {/* Section Label */}
              <p className="text-sm font-medium uppercase tracking-wider text-gray-600">
                Your Attorney
              </p>

              {/* Attorney Card - stacks on mobile, horizontal on sm+ */}
              <div className="flex flex-col sm:flex-row items-start gap-5">
                {/* Photo */}
                {getMediaUrl(partnerPhoto, 'card') && (
                  <div className="relative w-32 h-32 sm:w-36 sm:h-36 md:w-48 md:h-64 overflow-hidden rounded-xl shrink-0 block shadow-md">
                    <Image
                      src={getMediaUrl(partnerPhoto, 'card')}
                      alt={overviewPartner.name || ''}
                      fill
                      className="object-cover h-full w-full"
                    />
                  </div>
                )}

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <h3 className="text-xl md:text-2xl font-medium text-gray-900">
                    {overviewPartner.name}
                  </h3>
                  {overviewPartner.title && (
                    <p className="text-brand-gold font-medium mt-0.5">{overviewPartner.title}</p>
                  )}

                  {/* Partner Intro */}
                  {overviewPartnerIntro && (
                    <PartnerIntro
                      data={overviewPartnerIntro}
                      className="prose prose-sm max-w-none text-gray-600 mt-3 [&_p]:mb-0 [&_p]:leading-relaxed"
                    />
                  )}
                </div>
              </div>

              {/* CTA - full width below */}
              <Link
                href="/consultation"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-brand-blue text-white rounded-lg hover:bg-brand-blue/90 transition-colors font-medium text-sm"
              >
                Schedule a Consultation
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          )}
        </div>
      </div>
    </section>
  )
}
